import { useEffect, useState } from "react";
import { Download, FileText, Settings2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, Spinner } from "@/components/shared";

export default function Reports() {
  const [r, setR] = useState(null);
  const [band, setBand] = useState(null);
  const [active, setActive] = useState(true);

  useEffect(() => {
    api.get("/band/report").then(({ data }) => setR(data));
    api.get("/band/dashboard").then(({ data }) => { setBand(data.band); setActive(data.band.road_day_active); });
  }, []);

  const toggleRoadDay = async () => {
    const { data } = await api.put("/band/settings", { road_day_active: !active });
    setActive(data.road_day_active);
    toast.success(`Road Day ${data.road_day_active ? "activated" : "ended"}`);
  };

  const download = () => {
    const rows = Object.entries(r).map(([k, v]) => `${k},${v}`).join("\n");
    const blob = new Blob([`metric,value\n${rows}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "island-vibes-mas-report.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  if (!r) return <Spinner />;

  const stats = [
    { label: "Participants", value: r.participants },
    { label: "Passports Complete", value: r.passports_complete },
    { label: "Updates Sent", value: r.updates_sent },
    { label: "Help Requests", value: r.help_total },
    { label: "Help Resolved", value: r.help_resolved },
    { label: "Incidents", value: r.incidents },
  ];

  return (
    <Page>
      <h1 className="font-head font-extrabold text-3xl">Reports & Settings</h1>
      <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Post-event summary for {r.band}</p>

      <div className="cs-card p-5 mt-5">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 size={18} style={{ color: "var(--cs-magenta)" }} />
          <h2 className="font-head font-bold text-lg">Event Controls</h2>
        </div>
        <div className="flex items-center justify-between rounded-xl p-4" style={{ background: "var(--cs-card-hi)" }}>
          <div>
            <p className="font-semibold flex items-center gap-2"><Clock size={16} /> Road Day Mode</p>
            <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{band?.road_day_start} – {band?.road_day_end}. When active, participants see Road Day Mode first.</p>
          </div>
          <button data-testid="reports-toggle-roadday" onClick={toggleRoadDay} className="cs-tap rounded-full" style={{ width: 52, height: 30, background: active ? "var(--cs-green)" : "var(--cs-border)", position: "relative" }}>
            <span className="absolute rounded-full bg-white" style={{ width: 24, height: 24, top: 3, left: active ? 25 : 3, transition: "left .2s" }} />
          </button>
        </div>
      </div>

      <div className="cs-card p-5 mt-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><FileText size={18} style={{ color: "var(--cs-magenta)" }} /><h2 className="font-head font-bold text-lg">Post-Event Report</h2></div>
          <button data-testid="reports-download" onClick={download} className="cs-tap magenta-grad rounded-xl px-4 py-2 text-white font-semibold text-sm flex items-center gap-2">
            <Download size={16} /> CSV
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div key={s.label} data-testid={`report-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl p-4" style={{ background: "var(--cs-card-hi)" }}>
              <p className="font-head font-extrabold text-2xl">{s.value}</p>
              <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}
